/*
 * Custom multipack selector for Shopify Themes.
 * Configure offers with product tags such as `bundle:2:249` and `bundle:3:329`.
 * The offer price is presentational; Shopify automatic discounts enforce checkout pricing.
 */
class PackageBuilder extends HTMLElement {
  connectedCallback() {
    const configElement = this.querySelector('[data-package-builder-config]');
    if (!configElement) return;

    try {
      this.config = JSON.parse(configElement.textContent);
    } catch (error) {
      console.warn('Package builder configuration could not be read.', error);
      return;
    }

    this.offers = this.getOffers(this.config.tags || []);
    if (!this.offers.length) return;

    this.labels = this.getLabels();
    this.product = this.config.product;
    this.variantPicker = this.closest('variant-picker');
    this.productFormComponent = document.querySelector(
      `product-form-component[data-product-id="${this.product?.id}"]`
    );
    this.form = this.productFormComponent?.querySelector('form[data-type="add-to-cart-form"]');
    if (!this.variantPicker || !this.form || !this.product?.variants?.length) return;

    this.nativePickerForm = this.variantPicker.querySelector('.variant-picker__form');
    this.activeOffer = this.offers[0];
    this.render();
    this.addEventListener('click', this.onClick.bind(this));
    this.form.addEventListener('submit', this.onSubmit.bind(this), true);
    this.form.addEventListener('click', this.onFormClick.bind(this), true);
  }

  getOffers(tags) {
    return tags
      .map((tag) => {
        const match = String(tag).trim().match(/^bundle:(\d+):(\d+(?:\.\d+)?)(?::(\d+))?$/i);
        if (!match) return null;
        return {
          quantity: Number(match[1]),
          price: Number(match[2]),
          selectionCount: Number(match[3] || match[1]),
        };
      })
      .filter(Boolean)
      .filter((offer) => offer.quantity > 0 && offer.selectionCount > 0 && offer.price >= 0)
      .sort((a, b) => a.quantity - b.quantity);
  }

  getLabels() {
    const language = String(this.config.locale || document.documentElement.lang || 'en').toLowerCase().split('-')[0];
    const translations = {
      ro: { package: 'Pachet', item: 'Articol', unit: 'BUC.', packageItem: 'Articol pachet', chooseAvailable: 'Alegeți o opțiune disponibilă pentru fiecare articol.' },
      bg: { package: 'Пакет', item: 'Артикул', unit: 'БР.', packageItem: 'Артикул от пакета', chooseAvailable: 'Изберете налична опция за всеки артикул.' },
      es: { package: 'Paquete', item: 'Artículo', unit: 'UDS.', packageItem: 'Artículo del paquete', chooseAvailable: 'Elija una opción disponible para cada artículo.' },
      el: { package: 'Πακέτο', item: 'Προϊόν', unit: 'ΤΕΜ.', packageItem: 'Προϊόν πακέτου', chooseAvailable: 'Επιλέξτε μια διαθέσιμη επιλογή για κάθε προϊόν.' },
      fr: { package: 'Lot', item: 'Article', unit: 'PCS', packageItem: 'Article du lot', chooseAvailable: 'Choisissez une option disponible pour chaque article.' },
      de: { package: 'Paket', item: 'Artikel', unit: 'STK.', packageItem: 'Paketartikel', chooseAvailable: 'Wählen Sie für jeden Artikel eine verfügbare Option.' },
      it: { package: 'Pacchetto', item: 'Articolo', unit: 'PZ.', packageItem: 'Articolo del pacchetto', chooseAvailable: 'Scegli un’opzione disponibile per ogni articolo.' },
      pt: { package: 'Pacote', item: 'Artigo', unit: 'UN.', packageItem: 'Artigo do pacote', chooseAvailable: 'Escolha uma opção disponível para cada artigo.' },
      nl: { package: 'Pakket', item: 'Artikel', unit: 'ST.', packageItem: 'Pakketartikel', chooseAvailable: 'Kies voor elk artikel een beschikbare optie.' },
      pl: { package: 'Pakiet', item: 'Produkt', unit: 'SZT.', packageItem: 'Produkt w pakiecie', chooseAvailable: 'Wybierz dostępną opcję dla każdego produktu.' },
      hu: { package: 'Csomag', item: 'Tétel', unit: 'DB', packageItem: 'Csomag tétel', chooseAvailable: 'Válasszon elérhető opciót minden tételhez.' },
      hr: { package: 'Paket', item: 'Artikl', unit: 'KOM.', packageItem: 'Artikl paketa', chooseAvailable: 'Odaberite dostupnu opciju za svaki artikl.' },
      cs: { package: 'Balíček', item: 'Položka', unit: 'KS', packageItem: 'Položka balíčku', chooseAvailable: 'Vyberte dostupnou možnost pro každou položku.' },
      sk: { package: 'Balíček', item: 'Položka', unit: 'KS', packageItem: 'Položka balíčka', chooseAvailable: 'Vyberte dostupnú možnosť pre každú položku.' },
      lt: { package: 'Rinkinys', item: 'Prekė', unit: 'VNT.', packageItem: 'Rinkinio prekė', chooseAvailable: 'Pasirinkite galimą variantą kiekvienai prekei.' },
      et: { package: 'Komplekt', item: 'Toode', unit: 'TK', packageItem: 'Komplekti toode', chooseAvailable: 'Valige iga toote jaoks saadaolev valik.' },
      lv: { package: 'Komplekts', item: 'Prece', unit: 'GAB.', packageItem: 'Komplekta prece', chooseAvailable: 'Izvēlieties pieejamu opciju katrai precei.' },
      tr: { package: 'Paket', item: 'Ürün', unit: 'ADET', packageItem: 'Paket ürünü', chooseAvailable: 'Her ürün için uygun bir seçenek belirleyin.' },
      zh: { package: '套餐', item: '商品', unit: '件', packageItem: '套餐商品', chooseAvailable: '请为每件商品选择可售规格。' },
      ja: { package: 'セット', item: '商品', unit: '点', packageItem: 'セット商品', chooseAvailable: '各商品で在庫のあるオプションを選択してください。' },
      ko: { package: '세트', item: '상품', unit: '개', packageItem: '세트 상품', chooseAvailable: '각 상품의 판매 가능한 옵션을 선택하세요.' },
    };
    return translations[language] || { package: 'Package', item: 'Item', unit: 'PCS', packageItem: 'Package item', chooseAvailable: 'Please choose an available option for every item.' };
  }

  money(value) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: this.config.currency || 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    } catch (_) {
      return `${value} ${this.config.currency || ''}`.trim();
    }
  }

  offerLabel(offer) {
    return `${offer.quantity} ${this.labels.unit}`;
  }

  get startingVariant() {
    return this.product.variants.find((variant) => variant.available) || this.product.variants[0];
  }

  render() {
    this.hidden = false;
    this.innerHTML = `
      <fieldset class="package-picker" aria-label="${this.labels.package}">
        <legend>${this.labels.package}</legend>
        <div class="package-picker__offers">
          ${this.offers
            .map(
              (offer) => `
                <button type="button" class="package-card ${offer.quantity === this.activeOffer.quantity ? 'is-selected' : ''}" data-package-quantity="${offer.quantity}" aria-pressed="${offer.quantity === this.activeOffer.quantity}">
                  <span class="package-card__radio" aria-hidden="true"></span>
                  <span class="package-card__content"><span class="package-card__title">${this.offerLabel(offer)}</span></span>
                  <span class="package-card__price">${this.money(offer.price)}</span>
                </button>
              `
            )
            .join('')}
        </div>
      </fieldset>
      <div class="package-builder__components" ${this.activeOffer.quantity === 1 ? 'hidden' : ''}></div>
    `;
    this.setMode();
  }

  setMode() {
    const isMultipack = this.activeOffer.quantity > 1;
    this.nativePickerForm?.classList.toggle('package-builder__native-options-hidden', isMultipack);
    this.variantPicker?.classList.toggle('package-builder-active', isMultipack);
    this.productFormComponent?.classList.toggle('package-builder-active', isMultipack);
    if (isMultipack) this.renderComponents();
  }

  renderComponents() {
    const components = this.querySelector('.package-builder__components');
    const variant = this.startingVariant;
    const values = variant.options || [];
    components.innerHTML = Array.from({ length: this.activeOffer.selectionCount }, (_, componentIndex) => `
      <fieldset class="package-builder__component" data-component-index="${componentIndex}">
        <legend>${this.labels.item} ${componentIndex + 1}</legend>
        ${this.product.options
          .map(
            (optionName, optionIndex) => `
              <div class="package-builder__option" data-option-index="${optionIndex}">
                <span class="package-builder__option-label">${this.escape(optionName)}</span>
                <div class="package-builder__values">
                  ${[...new Set(this.product.variants.map((item) => item.options[optionIndex]))]
                    .map(
                      (value) => `<button type="button" class="package-builder__value ${value === values[optionIndex] ? 'is-selected' : ''}" data-value="${this.escape(value)}">${this.escape(value)}</button>`
                    )
                    .join('')}
                </div>
              </div>
            `
          )
          .join('')}
      </fieldset>
    `).join('');
  }

  escape(value) {
    const element = document.createElement('span');
    element.textContent = value;
    return element.innerHTML;
  }

  onClick(event) {
    const offerButton = event.target.closest('[data-package-quantity]');
    if (offerButton) {
      this.activeOffer = this.offers.find((offer) => offer.quantity === Number(offerButton.dataset.packageQuantity));
      this.render();
      return;
    }

    const valueButton = event.target.closest('.package-builder__value');
    if (valueButton) {
      valueButton.parentElement.querySelectorAll('.package-builder__value').forEach((button) => button.classList.remove('is-selected'));
      valueButton.classList.add('is-selected');
    }
  }

  selectedVariants() {
    return [...this.querySelectorAll('.package-builder__component')].map((component) => {
      const selections = [...component.querySelectorAll('.package-builder__option')].map((option) => option.querySelector('.package-builder__value.is-selected')?.dataset.value);
      return this.product.variants.find((variant) => variant.available && variant.options.every((value, index) => value === selections[index]));
    });
  }

  onSubmit(event) {
    if (this.activeOffer.quantity === 1) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.addPackage(false);
  }

  onFormClick(event) {
    if (this.activeOffer.quantity === 1) return;
    if (!event.target.closest('.shopify-payment-button, .accelerated-checkout-block')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.addPackage(true);
  }

  async addPackage(goToCheckout) {
    const variants = this.selectedVariants();
    if (variants.some((variant) => !variant)) {
      window.alert(this.labels.chooseAvailable);
      return;
    }

    const bundleId = `bundle-${Date.now()}`;
    const items = variants.map((variant, index) => ({
      id: variant.id,
      quantity: 1,
      properties: {
        [this.labels.package]: this.offerLabel(this.activeOffer),
        [this.labels.packageItem]: `${index + 1} / ${this.activeOffer.selectionCount}`,
        '_package_id': bundleId,
      },
    }));

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Package could not be added.');
      window.location.href = goToCheckout ? '/checkout' : '/cart';
    } catch (error) {
      console.error(error);
      window.alert(error.message || 'Package could not be added.');
    }
  }
}

if (!customElements.get('package-builder')) customElements.define('package-builder', PackageBuilder);
